import { useEffect, useRef, useState } from "react";

const DRAG_THRESHOLD_PX = 3;

export default function SliderField({ label, unit, value, min, max, step, onChange, prefix, compact }) {
  const [textValue, setTextValue] = useState(String(value));
  const [editing, setEditing] = useState(false);
  const cardRef = useRef(null);
  const inputRef = useRef(null);
  const dragState = useRef(null);

  useEffect(() => {
    if (!editing) setTextValue(String(value));
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commitText = (raw) => {
    const parsed = parseFloat(raw);
    if (Number.isNaN(parsed)) {
      setTextValue(String(value));
      return;
    }
    const clamped = Math.min(max, Math.max(min, parsed));
    onChange(clamped);
    setTextValue(String(clamped));
  };

  const decimals = step < 1 ? String(step).split(".")[1]?.length ?? 1 : 0;

  const clampToStep = (raw) => {
    const clamped = Math.min(max, Math.max(min, raw));
    const stepped = Math.round(clamped / step) * step;
    return Number(stepped.toFixed(6));
  };

  const handlePointerDown = (e) => {
    if (editing) return;
    const card = cardRef.current;
    card.setPointerCapture(e.pointerId);
    dragState.current = {
      startX: e.clientX,
      startValue: value,
      dragged: false,
      pointerId: e.pointerId,
    };
  };

  const handlePointerMove = (e) => {
    const drag = dragState.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    if (Math.abs(dx) > DRAG_THRESHOLD_PX) drag.dragged = true;
    if (!drag.dragged) return;

    const range = max - min;
    const cardWidth = cardRef.current.getBoundingClientRect().width || 1;
    // Full card width drag covers the full range; step-aligned.
    const delta = (dx / cardWidth) * range;
    const next = clampToStep(drag.startValue + delta);
    if (next !== value) onChange(next);
  };

  const endDrag = (e) => {
    const drag = dragState.current;
    if (!drag) return;
    cardRef.current.releasePointerCapture(drag.pointerId);
    const wasDrag = drag.dragged;
    dragState.current = null;
    if (!wasDrag) {
      setEditing(true);
    }
  };

  const fillPct = Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));

  return (
    <div className={`slider-card${compact ? " slider-card-compact" : ""}`}>
      {!compact && (
        <div className="slider-card-header">
          <span className="slider-card-label">{label}</span>
          {unit && <span className="slider-card-unit">{unit}</span>}
        </div>
      )}
      <div
        ref={cardRef}
        className={`slider-card-body${editing ? " is-editing" : ""}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div className="slider-card-fill" style={{ width: `${fillPct}%` }} />
        {editing ? (
          <input
            ref={inputRef}
            className="slider-card-input"
            type="text"
            inputMode="decimal"
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            onBlur={() => {
              commitText(textValue);
              setEditing(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                commitText(textValue);
                setEditing(false);
              } else if (e.key === "Escape") {
                setTextValue(String(value));
                setEditing(false);
              }
            }}
          />
        ) : (
          <>
            <span className="slider-card-hint">{prefix ?? "drag"}</span>
            <span className="slider-card-value">{Number(value).toFixed(decimals)}</span>
          </>
        )}
      </div>
    </div>
  );
}
